import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, { useEffect } from 'react'
import useAddExtension from './useAddExtension'
import './AddExtensionStyle.css'
import useCommunicationsLeaveGuard from '../../shared/useCommunicationsLeaveGuard'
import UnsavedChangesModal from '../../UnsavedChangesModal/UnsavedChangesModal.component'
import {
  isSettingFieldChanged,
  settingsFieldClassName,
} from '@utils/settings.util'
import {
  Breadcrumbs,
  Button,
  InputField,
  Select,
} from '@enamelai/design-system'

const AddExtension = (): React.ReactElement | null => {
  const { teams } = useTeamCatalog()
  const {
    baseline,
    cancelToList,
    discardUnsavedChanges,
    onClickCancel,
    onHide,
    showUnsavedChangesModal,
    extensionData,
    handleChange,
    hasUnsavedChanges,
    handleSave,
    setHeaderButtons,
    setHeaderTitle,
    validationErrors,
  } = useAddExtension()
  const leaveGuard = useCommunicationsLeaveGuard(hasUnsavedChanges())

  useEffect(() => {
    setHeaderButtons(
      <>
        <Button size="sm" variant="secondary" onClick={onClickCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={Object.keys(validationErrors).length > 0}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    setHeaderTitle(
      <Breadcrumbs
        items={[
          { label: 'Phone management', onClick: onClickCancel },
          { label: 'Phone numbers' },
        ]}
        showBackIcon
        onBackClick={onClickCancel}
      />,
    )
    return () => {
      setHeaderButtons(null)
      setHeaderTitle(null)
    }
  }, [
    setHeaderButtons,
    setHeaderTitle,
    handleSave,
    cancelToList,
    validationErrors,
  ])

  return (
    <>
      <div className="scp-pm-add-extension">
        <InputField
          variant="text"
          fullWidth
          label="Name"
          className={settingsFieldClassName(
            isSettingFieldChanged(extensionData.name, baseline.name),
          )}
          value={extensionData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          helperText={validationErrors.name || undefined}
          error={!!validationErrors.name}
        />
        <InputField
          variant="phone"
          fullWidth
          label="Phone number"
          optional
          className={settingsFieldClassName(
            isSettingFieldChanged(
              extensionData.phone_number ?? '',
              baseline.phone_number ?? '',
            ),
          )}
          value={extensionData.phone_number ?? ''}
          placeholder="(555) 555-5555"
          onChange={(e) => handleChange('phone_number', e.target.value)}
          helperText={validationErrors.phone_number || undefined}
          error={!!validationErrors.phone_number}
        />
        <Select
          label="Team"
          value={
            extensionData.team != null &&
            String(extensionData.team).trim() !== ''
              ? extensionData.team
              : undefined
          }
          onChange={(value) => handleChange('team', String(value ?? ''))}
          options={teams.map((team) => ({
            label: team,
            value: team,
          }))}
          placeholder="Select team"
          helperText={validationErrors.team || undefined}
          error={!!validationErrors.team}
        />
        <InputField
          variant="text"
          fullWidth
          label="Extension number"
          className={settingsFieldClassName(
            isSettingFieldChanged(
              extensionData.extension_number,
              baseline.extension_number,
            ),
          )}
          value={extensionData.extension_number}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '')
            if (value.length > 3) {
              return
            }
            handleChange('extension_number', value)
          }}
          placeholder="100"
          helperText={validationErrors.extension_number || undefined}
          error={!!validationErrors.extension_number}
        />
      </div>
      {leaveGuard.modal}
      <UnsavedChangesModal
        show={showUnsavedChangesModal}
        onConfirm={discardUnsavedChanges}
        onHide={onHide}
      />
    </>
  )
}

export default withTeamCatalog(AddExtension)
